import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import {
  Box, Typography, TextField, InputAdornment, Button,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import SearchIcon from '@mui/icons-material/Search';
import EventOutlinedIcon from '@mui/icons-material/EventOutlined';
import EventCard from './EventCard';
import CreateEventModal from './CreateEventModal';
import LocalText from '../common/LocalText';
import { useLocalText } from '../common/useLocalText';
import ConfirmModal from '../form-components/ConfirmModal';
import StatusTabs from '../approval/StatusTabs';
import ContentPreviewDrawer from '../approval/ContentPreviewDrawer';
import {
  getEvents, createEvent, updateEvent, deleteEvent, getEventFormats,
} from '../../api/eventApi';
import { submitApproval } from '../../api/approvalApi';
import { useSpaceId } from '../../hooks/useSpaceId';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import { usePermissions } from '../../hooks/usePermissions';
import { CONTENT_TYPES } from '../../constants/contentTypes';
import { signalRService } from '../../services/signalRService';
import { SIGNALR_EVENTS } from '../../constants/signalREvents';

const eventName = (e) => e?.localizedName?.values?.[0]?.value ?? '';

const EventList = () => {
  const spaceId = useSpaceId();
  const { isAuthenticated } = useAuth();
  const { toast } = useToast();
  const { canFeature } = usePermissions();
  const canEdit = canFeature('/events', 'event-edit');
  const searchPlaceholder = useLocalText('Search events…');

  const [events,              setEvents]              = useState([]);
  const [eventFormats,        setEventFormats]        = useState([]);
  const [search,              setSearch]              = useState('');
  const [debouncedSearch,     setDebouncedSearch]     = useState('');
  const [tabValue,            setTabValue]            = useState('live');
  const [highlight,           setHighlight]           = useState({ tab: null, token: 0 });
  const flashTab = useCallback((tab) => setHighlight({ tab, token: Date.now() }), []);
  const [modalOpen,           setModalOpen]           = useState(false);
  const [editTarget,          setEditTarget]          = useState(null);
  const [approvalMode,        setApprovalMode]        = useState(false);
  const [viewTarget,          setViewTarget]          = useState(null);
  const [deleteTarget,        setDeleteTarget]        = useState(null);
  const [proposeDeleteTarget, setProposeDeleteTarget] = useState(null);
  const [viewProposalOpen,    setViewProposalOpen]    = useState(false);
  const [viewApprovalRecord,  setViewApprovalRecord]  = useState(null);
  const timerRef = useRef(null);

  const load = useCallback(() => getEvents().then(setEvents).catch(() => {}), []);

  useEffect(() => { if (spaceId) load(); }, [spaceId, load]);
  useEffect(() => () => clearTimeout(timerRef.current), []);

  // Fetched once here (not per-card) so N event cards don't fire N duplicate lookups.
  useEffect(() => {
    getEventFormats().then(setEventFormats).catch(() => setEventFormats([]));
  }, []);

  // Real-time refresh when approvals are decided or withdrawn
  useEffect(() => {
    if (!isAuthenticated) return;
    const refresh = () => load();
    signalRService.on(SIGNALR_EVENTS.APPROVAL_DECISION, refresh);
    signalRService.on(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, refresh);
    return () => {
      signalRService.off(SIGNALR_EVENTS.APPROVAL_DECISION, refresh);
      signalRService.off(SIGNALR_EVENTS.APPROVAL_WITHDRAWN, refresh);
    };
  }, [isAuthenticated, load]);

  const handleSearchChange = (e) => {
    const v = e.target.value;
    setSearch(v);
    clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => setDebouncedSearch(v), 250);
  };

  const searchFiltered = useMemo(() => {
    const q = debouncedSearch.trim().toLowerCase();
    if (!q) return events;
    return events.filter(
      (e) => e.localizedName?.values?.some((v) => v.value.toLowerCase().includes(q))
    );
  }, [events, debouncedSearch]);

  const tabCounts = useMemo(() => ({
    draft:    events.filter((e) => e.contentStatus === 'draft' && e.approvalStatus !== 'pending').length,
    pending:  events.filter((e) => e.approvalStatus === 'pending').length,
    live:     events.filter((e) => e.contentStatus === 'approved').length,
    rejected: events.filter((e) => e.approvalStatus === 'rejected').length,
  }), [events]);

  const tabFiltered = useMemo(() => {
    switch (tabValue) {
      case 'draft':    return searchFiltered.filter((e) => e.contentStatus === 'draft' && e.approvalStatus !== 'pending');
      case 'pending':  return searchFiltered.filter((e) => e.approvalStatus === 'pending');
      case 'live':     return searchFiltered.filter((e) => e.contentStatus === 'approved');
      case 'rejected': return searchFiltered.filter((e) => e.approvalStatus === 'rejected');
      default:         return searchFiltered;
    }
  }, [searchFiltered, tabValue]);

  // Golden rule: direct edit only when contentStatus='draft' AND no ca record
  const handleSave = async (data) => {
    try {
      if (editTarget && approvalMode) {
        await submitApproval({
          contentType:      CONTENT_TYPES.EVENT,
          contentId:        editTarget._liveId ?? editTarget.id,
          contentName:      eventName(editTarget),
          actionType:       'edit',
          proposedSnapshot: JSON.stringify(data),
        });
        toast.success('Edit proposal submitted for approval.');
      } else if (editTarget) {
        await updateEvent(editTarget.id, data);
        toast.success('Event updated.');
      } else {
        await createEvent(data);
        toast.success('Event created and moved to Draft.');
      }
      const targetTab = approvalMode ? 'pending' : 'draft';
      setEditTarget(null);
      setApprovalMode(false);
      await load();
      flashTab(targetTab);
    } catch (err) {
      throw err; // global api-error handler shows toast; modal stays open
    }
  };

  const handleEdit = (event) => {
    const canDirect = event.contentStatus === 'draft' && !event.approvalId;
    setApprovalMode(!canDirect);
    setEditTarget(event);
    setModalOpen(true);
  };

  const handleDelete = (event) => setDeleteTarget(event);
  const handleProposeDelete = (event) => setProposeDeleteTarget(event);

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    try {
      await deleteEvent(deleteTarget.id);
      setDeleteTarget(null);
      toast.success('Event deleted.');
      await load();
    } catch {
      setDeleteTarget(null);
    }
  };

  const confirmProposeDelete = async () => {
    if (!proposeDeleteTarget) return;
    try {
      await submitApproval({
        contentType:  CONTENT_TYPES.EVENT,
        contentId:    proposeDeleteTarget.id,
        contentName:  eventName(proposeDeleteTarget),
        actionType:   'delete',
      });
      toast.success('Deletion request submitted for approval.');
      setProposeDeleteTarget(null);
      await load();
      flashTab('pending');
    } catch {
      setProposeDeleteTarget(null);
    }
  };

  const openViewDrawer = (event) => {
    setViewApprovalRecord({
      approvalId:   event.approvalId,
      actionType:   event.approvalActionType,
      contentType:  CONTENT_TYPES.EVENT,
      sectionLabel: 'Event',
      contentName:  eventName(event),
      status:       event.approvalStatus,
      _liveId:      event.id,
    });
    setViewProposalOpen(true);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditTarget(null);
    setApprovalMode(false);
  };

  return (
    <Box>
      {/* Toolbar */}
      <Box
        display="flex" alignItems="center" justifyContent="space-between"
        flexWrap="wrap" gap={2} mb={3}
      >
        <Box display="flex" alignItems="center" gap={2}>
          <TextField
            size="small"
            placeholder={searchPlaceholder}
            value={search}
            onChange={handleSearchChange}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ fontSize: 18, color: '#85a5c6' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              width: { xs: '100%', sm: 260 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '12px', bgcolor: 'white',
                '& fieldset': { borderColor: '#dde6f0' },
                '&:hover fieldset': { borderColor: '#5383b3' },
                '&.Mui-focused fieldset': { borderColor: '#0a326d' },
              },
            }}
          />
          <Typography variant="caption" sx={{ color: '#85a5c6', fontWeight: 600 }}>
            {tabFiltered.length} {tabFiltered.length === 1
              ? <LocalText>event</LocalText>
              : <LocalText>events</LocalText>}
          </Typography>
        </Box>

        {canEdit && (
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={(e) => { e.currentTarget.blur(); setModalOpen(true); }}
            sx={{
              bgcolor: '#0a326d', color: 'white', borderRadius: '12px',
              px: 2.5, py: 1, fontWeight: 600, fontSize: 13, textTransform: 'none',
              boxShadow: '0 4px 12px rgba(10,50,109,0.25)',
              '&:hover': { bgcolor: '#0d3d85', boxShadow: '0 6px 16px rgba(10,50,109,0.35)' },
            }}
          >
            <LocalText>New Event</LocalText>
          </Button>
        )}
      </Box>

      {/* Status filter tabs */}
      <StatusTabs
        counts={tabCounts}
        value={tabValue}
        onChange={setTabValue}
        items={events}
        highlightTab={highlight.tab}
        highlightToken={highlight.token}
      />

      {/* Empty state */}
      {tabFiltered.length === 0 && (
        <Box sx={{ textAlign: 'center', py: 10 }}>
          <EventOutlinedIcon sx={{ fontSize: 56, color: '#c8d8ea', mb: 1.5 }} />
          <Typography variant="body1" sx={{ color: '#85a5c6', fontWeight: 500 }}>
            {debouncedSearch
              ? <LocalText>No events match your search.</LocalText>
              : <LocalText>No events yet. Add one!</LocalText>}
          </Typography>
        </Box>
      )}

      {/* Card grid */}
      <Box
        sx={{
          display: 'grid',
          gridTemplateColumns: {
            xs: 'minmax(0, 1fr)',
            sm: 'repeat(2, minmax(0, 1fr))',
            md: 'repeat(3, minmax(0, 1fr))',
            lg: 'repeat(4, minmax(0, 1fr))',
          },
          gap: 2.5,
        }}
      >
        {tabFiltered.map((e) => (
          <EventCard
            key={e.id}
            event={e}
            eventFormats={eventFormats}
            onEdit={handleEdit}
            onDelete={handleDelete}
            onProposeDelete={handleProposeDelete}
            onViewProposal={openViewDrawer}
            onViewReadonly={(event) => setViewTarget(event)}
            onSuccess={(targetTab) => { load(); if (targetTab) flashTab(targetTab); }}
            showActions={canEdit}
          />
        ))}
      </Box>

      {/* Create / Edit modal */}
      <CreateEventModal
        open={modalOpen}
        onClose={handleModalClose}
        onSave={handleSave}
        initial={editTarget}
        approvalMode={approvalMode}
      />

      {/* Readonly view modal (eye icon on locked cards) */}
      <CreateEventModal
        open={Boolean(viewTarget)}
        onClose={() => setViewTarget(null)}
        onSave={() => {}}
        initial={viewTarget}
        readOnly
      />

      {/* Direct delete confirm */}
      <ConfirmModal
        open={Boolean(deleteTarget)}
        onCancel={() => setDeleteTarget(null)}
        onConfirm={confirmDelete}
        severity="danger"
        title="Delete Event"
        message={`Delete "${deleteTarget?.localizedName?.values?.[0]?.value ?? 'this event'}"?`}
        confirmLabel="Delete"
      />

      {/* Propose-delete confirm */}
      <ConfirmModal
        open={Boolean(proposeDeleteTarget)}
        onCancel={() => setProposeDeleteTarget(null)}
        onConfirm={confirmProposeDelete}
        severity="warning"
        title="Request Deletion"
        message={`Submit a deletion request for "${eventName(proposeDeleteTarget) || 'this event'}"? It will remain active until an approver approves.`}
        confirmLabel="Submit for Approval"
      />

      {/* Comparison view drawer (pending/rejected) */}
      {viewApprovalRecord && (
        <ContentPreviewDrawer
          open={viewProposalOpen}
          approvalRecord={viewApprovalRecord}
          onClose={() => { setViewProposalOpen(false); setViewApprovalRecord(null); }}
          readOnly
          onEdit={(proposedSnap) => {
            setViewProposalOpen(false);
            const liveId = viewApprovalRecord._liveId;
            setEditTarget({ ...(proposedSnap || {}), id: liveId, _liveId: liveId });
            setApprovalMode(true);
            setViewApprovalRecord(null);
            setModalOpen(true);
          }}
        />
      )}
    </Box>
  );
};

export default EventList;
